import { z } from 'zod';
import { ExpenseVatTreatmentSchema } from '../vat.js';
import { CalendarDateOnlySchema, LowercaseUuidSchema, WarningDetailSchema } from './common.js';
import { SuggestedExpenseAccountSchema } from './supplier-invoice.js';
// Correcting the matter, account or VAT treatment of an APPROVED or PAID
// expense. One change set drives both the preview (what would happen) and the
// apply. Without a stated VAT treatment the service decides it (see `vat.reason`).
export const ReclassifyLineLedgerAccountSchema = z.strictObject({
    lineItemId: LowercaseUuidSchema,
    /** The account this line posts to; null makes the line use the header account. */
    ledgerAccountId: LowercaseUuidSchema.nullable(),
});
const reclassifyChangeFields = {
    /** The new matter; null makes the expense firm overhead. Omit to leave it unchanged. */
    clientProjectId: LowercaseUuidSchema.nullable().optional(),
    /** The new header account (Accounting only). Omit to let the default account follow a move between overhead and a matter. */
    ledgerAccountId: LowercaseUuidSchema.optional(),
    /** Per-line accounts (Accounting only). Lines not listed are unchanged. */
    lineLedgerAccounts: z.array(ReclassifyLineLedgerAccountSchema).max(500).optional(),
    /** Claim the input VAT or not (Accounting only). Omit to follow the header account's default when it changes, else keep what was posted. */
    vatTreatment: ExpenseVatTreatmentSchema.optional(),
};
/** What to change on a posted expense; omitted fields stay as they are. */
export const SupplierInvoiceReclassifyChangesSchema = z.strictObject(reclassifyChangeFields);
export const ReclassifySupplierInvoiceInputSchema = z.strictObject({
    ...reclassifyChangeFields,
    idempotencyKey: z.string().min(1, 'Idempotency key is required'),
});
export const ReclassifyRefusalCodeSchema = z.enum([
    'not-posted',
    'no-journal',
    'no-line-items',
    'on-draft-invoice',
    'on-issued-invoice',
    'credit-note',
    'legacy-settlement',
    'opening-creditor',
    'period-locked',
    'accounting-required',
    'account-unusable',
    'vat-treatment-required',
    'payment-funding-unusable',
]);
export const ReclassifyAccountRefSchema = z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
});
/** A matter with its client; every field null for firm overhead. */
export const ReclassifyClientProjectRefSchema = z.object({
    clientProjectId: z.string().nullable(),
    clientProjectName: z.string().nullable(),
    clientId: z.string().nullable(),
    clientName: z.string().nullable(),
});
export const ReclassifyVatPostureSchema = z.enum(['claimed', 'not-claimed']);
/**
 * Why the corrected expense claims its VAT or not:
 * - `no-vat`: the firm is not VAT-registered, or the expense carries no VAT.
 * - `chosen`: the request stated the treatment.
 * - `account-default`: the header account changes, so the VAT follows its default treatment.
 * - `kept`: the header account stays, so what was posted stands.
 */
export const ReclassifyVatReasonSchema = z.enum(['no-vat', 'chosen', 'account-default', 'kept']);
export const ReclassifyRefusalSchema = z.object({
    code: ReclassifyRefusalCodeSchema,
    /** Plain wording, safe to show as is; the same text an apply is refused with. */
    message: z.string(),
    /** The client invoice the expense sits on, for the two invoice refusals. */
    invoice: z
        .object({
        id: z.string(),
        status: z.string(),
        clientId: z.string().nullable(),
        clientProjectId: z.string().nullable(),
        date: CalendarDateOnlySchema,
    })
        .nullable()
        .optional(),
});
/**
 * What a correction would do, computed without writing anything. A refusal is
 * data here so a surface can show it and disable saving; applying the same
 * changes is refused with the same message. When refused, `clientProject`,
 * `ledgerAccount`, `lineItems` and `payments` describe no change.
 */
export const SupplierInvoiceReclassifyPreviewSchema = z.object({
    supplierInvoiceId: z.string(),
    /** The ledger already reads as requested and nothing is behind: applying writes nothing. */
    noop: z.boolean(),
    refusal: ReclassifyRefusalSchema.nullable(),
    /** Conduct warnings that do not stop the correction (for example a VAT month that may be filed). */
    warningDetails: z.array(WarningDetailSchema),
    clientProject: z.object({
        from: ReclassifyClientProjectRefSchema,
        to: ReclassifyClientProjectRefSchema,
        changed: z.boolean(),
    }),
    ledgerAccount: z.object({
        from: ReclassifyAccountRefSchema.nullable(),
        to: ReclassifyAccountRefSchema.nullable(),
        changed: z.boolean(),
        /**
         * `suggested` when a move to a business cost lands on the supplier's suggested
         * expense account, `default` when the resulting account is the role default
         * for a matter or a business cost, otherwise `chosen`.
         */
        source: z.enum(['default', 'suggested', 'chosen']),
        /** The supplier history behind a `suggested` account; null for any other source. */
        suggestion: SuggestedExpenseAccountSchema.nullable(),
    }),
    /** Only the lines whose own account changes. `null` means the line uses the header account. */
    lineItems: z.array(z.object({
        lineItemId: z.string(),
        description: z.string().nullable(),
        from: ReclassifyAccountRefSchema.nullable(),
        to: ReclassifyAccountRefSchema.nullable(),
    })),
    vat: z.object({
        registered: z.boolean(),
        /** The expense's input VAT. */
        inputVatMinorUnits: z.number().int(),
        postedPosture: ReclassifyVatPostureSchema,
        resultingPosture: ReclassifyVatPostureSchema,
        /** What the resulting header account's default VAT treatment would do; null when it has none. */
        accountDefaultPosture: ReclassifyVatPostureSchema.nullable(),
        reason: ReclassifyVatReasonSchema,
    }),
    /** Payments whose settlement journal is reposted because the matter changes. */
    payments: z.array(z.object({
        paymentId: z.string(),
        paidAt: CalendarDateOnlySchema,
        amountMinorUnits: z.number().int(),
    })),
});
